import { useMutation } from '@apollo/client';
import React, { useState } from 'react';
import Button from '../../components/Button';
import Form, { Field, Submit } from '../../components/Form';
import type { AllowPublicContentBy } from '../../graphql/generated/graphql';
import SettingCard from '../../settings/SettingCard';
import useAllowPublicContentBy from '../../utils/useAllowPublicContentBy';
import styles from './PublicContentSettings.module.scss';
import { gql } from '../../graphql/generated/gql';

const UPDATE_ALLOW_PUBLIC_CONTENT_BY_MUTATION = gql(`
  mutation updatePublicContentBySetting(
    $allowPublicContentBy: AllowPublicContentBy!
  ) {
    organization: updateOrganization(
      settings: { allowPublicContentBy: $allowPublicContentBy }
    ) {
      id
      settings {
        allowPublicContentBy
      }
    }
  }
`);

const options = {
  ONLY_ADMINS: 'Only admins can make posts and topics public',
  ADMIN_TOPICS_USER_POSTS:
    'Only admins can make topics public, anyone can make posts public',
  ALL: 'Anyone can make posts and topics public',
} as const;

type Values = {
  allowPublicContentBy: AllowPublicContentBy | null;
};

const PublicContentSettings = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [updateSettings] = useMutation(UPDATE_ALLOW_PUBLIC_CONTENT_BY_MUTATION);

  const allowPublicContentBy = useAllowPublicContentBy();

  const handleSubmit = async (values: Values) => {
    if (values.allowPublicContentBy) {
      await updateSettings({
        variables: { allowPublicContentBy: values.allowPublicContentBy },
      });
      setIsEditing(false);
    }
  };

  return (
    <>
      {isEditing ? (
        <SettingCard
          title="Public topics & posts"
          description={
            <Form<Values>
              initialValues={{ allowPublicContentBy }}
              onSubmit={handleSubmit}
            >
              <div className={styles.formFields}>
                {(
                  ['ALL', 'ADMIN_TOPICS_USER_POSTS', 'ONLY_ADMINS'] as const
                ).map((value) => (
                  <Field
                    key={value}
                    label={options[value]}
                    name="allowPublicContentBy"
                    type="radio"
                    value={value}
                  />
                ))}
              </div>
              <div className={styles.description}>
                Regardless of selection, users must have edit access to the
                topic or post to make it public.
              </div>
              <div className={styles.formFooter}>
                <Submit variant="primary">Save</Submit>
              </div>
            </Form>
          }
        />
      ) : (
        <SettingCard
          actions={
            <Button onClick={() => setIsEditing(true)} variant="secondary">
              Edit
            </Button>
          }
          title="Public topics & posts"
          description={allowPublicContentBy && options[allowPublicContentBy]}
        />
      )}
    </>
  );
};

export default PublicContentSettings;
